import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { ExternalLink, ArrowRight } from 'lucide-react';

import { getLangKey } from 'models/Language';
import { HelpGuideBanner } from 'appJS/components/HelpGuideBanner';
import usePusherSubscribe from 'appJS/hooks/usePusherSubscribe';
import { Event, Subscription } from 'appJS/models/Pusher';
import { getProjectTaskCount } from 'api/ProjectApi';

import * as translations from '../strings';
import { WelcomeProject, demoSiteUrlWithApiKey } from '../types';
import styles from '../index.module.css';

type Props = {
  project: WelcomeProject;
  // Live devurl from parent state — reflects the URL saved in Step 1, which
  // is more current than project.devurl from the initial server render.
  devurl: string | null;
  // When true the user opted into the demo site; BugHerd is loaded there via
  // the project's API key passed in the query string, so there's nothing to
  // install on their end.
  isDemo?: boolean;
  pusherApiKey: string;
  pusherChannelAuthEndpoint: string;
};

const TryBugHerd: React.FC<Props> = ({
  project,
  devurl,
  isDemo = false,
  pusherApiKey,
  pusherChannelAuthEndpoint
}) => {
  const strings = translations[getLangKey()];

  // The fallback banner is a safety net: only revealed once the user has tried
  // to open BugHerd on their site, in case they can't complete the step there.
  const [showFallback, setShowFallback] = useState<boolean>(false);

  // Revealed once the user creates their first task — the activation moment.
  // We then surface the "Go to your board" hand-off to the kanban + tour.
  const [taskCreated, setTaskCreated] = useState<boolean>(false);

  // The demo site reads the project's API key from the query string and
  // injects the BugHerd script at runtime, so the user can try it without
  // installing anything themselves.
  const openUrl =
    isDemo && project.api_key
      ? demoSiteUrlWithApiKey(project.api_key)
      : devurl;

  // Live detection: the project's Pusher channel fires `task_create` when the
  // user drops their first feedback pin on their site. KANBAN subscription is
  // used (rather than USER_ACTIVITIES/USER_PROJECTS) so no legacy default
  // handlers are bound — we only want our own task_create listener.
  usePusherSubscribe({
    pusher: {
      config: { pusherApiKey, pusherChannelAuthEndpoint },
      projects: [
        {
          id: String(project.id),
          pusherChannelName: project.pusherChannelName
        }
      ],
      events: [{ name: Event.TASK_CREATE, onUpdate: () => setTaskCreated(true) }],
      subscription: Subscription.KANBAN
    },
    loading: false
  });

  // Resume case: if a task already exists (e.g. the user created one then
  // refreshed this page), reveal the hand-off immediately without waiting for
  // a live event.
  useEffect(() => {
    let active = true;
    getProjectTaskCount(project.id)
      .then(data => {
        if (active && (data?.tasks_count ?? 0) > 0) setTaskCreated(true);
      })
      .catch(() => {
        // Non-fatal: fall back to live Pusher detection only.
      });
    return () => {
      active = false;
    };
  }, [project.id]);

  const handleOpenWebsite = () => {
    if (openUrl) {
      window.open(openUrl, '_blank');
    }
    setShowFallback(true);
  };

  const goToBoard = () => {
    window.location.href = `/projects/${project.id}/kanban`;
  };

  return (
    <>
      <div className={styles.stepPill}>STEP 3</div>
      <h2 className={styles.stepHeading}>{strings.tryHeading}</h2>
      <div className={styles.tryBody}>
        <p style={{ margin: 0 }}>
          BugHerd is ready on your site. See for yourself{' '}
          <strong>how easy it is for your clients to give feedback</strong>.
        </p>
        <p style={{ margin: 0 }}>
          Once you&apos;re there, hit the &lsquo;+&rsquo; button in the sidebar
          and click anywhere on the page to leave your first piece of feedback.
        </p>
      </div>

      <div className={styles.variantTryButtonArea}>
        <div className={styles.variantTryButtonInner}>
          <Button
            type={taskCreated ? 'default' : 'primary'}
            shape="round"
            size="large"
            icon={<ExternalLink size={15} />}
            onClick={handleOpenWebsite}
            disabled={!openUrl}
          >
            {strings.openOnWebsite}
          </Button>
          {!taskCreated && <span className={styles.greenDot} />}
        </div>
      </div>

      {taskCreated && (
        <div className={styles.variantTryButtonArea}>
          <Button
            type="primary"
            shape="round"
            size="large"
            icon={<ArrowRight size={15} />}
            onClick={goToBoard}
          >
            {strings.goToBoard}
          </Button>
        </div>
      )}

      {showFallback && !taskCreated && (
        <div className={styles.variantTryFallback}>
          <HelpGuideBanner
            bubbleContent="💡"
            header={strings.tryLaterHeading}
            button={{
              label: strings.tryLaterButton,
              action: () => {
                // Dismisses the onboarding flow (records the dismissed event)
                // then server-redirects to the project's getting-started checklist.
                window.location.href = '/setup/dismiss';
              }
            }}
          />
        </div>
      )}
    </>
  );
};

export default TryBugHerd;
